/**
 * AsyncSelect Component
 * 
 * A wrapper around the Select component that supports lazy loading of options.
 * Options are only loaded when the dropdown is opened, reducing initial API calls
 * and preventing database connection exhaustion.
 */

import React, { useState, useCallback, useRef } from 'react';
import { Select } from '../../design-system';
import type { SelectProps, SelectOption } from '../../design-system';
import styled from 'styled-components';

export interface AsyncSelectProps extends Omit<SelectProps, 'options'> {
  /**
   * Function to load options asynchronously when dropdown opens
   */
  loadOptions: () => Promise<SelectOption[]>;
  
  /**
   * Initial options to display (optional)
   */
  initialOptions?: SelectOption[];
  
  /**
   * Whether to cache loaded options
   */
  cacheOptions?: boolean;
  
  /**
   * Loading message to show while fetching options
   */
  loadingMessage?: string;
  
  /**
   * Error message to show if loading fails
   */
  errorMessage?: string;
}

const LoadingOption = styled.div`
  padding: 8px 12px;
  color: var(--text-secondary);
  font-style: italic;
`;

const ErrorOption = styled.div`
  padding: 8px 12px;
  color: var(--error-600);
`;

/**
 * AsyncSelect component that loads options on demand
 */
export const AsyncSelect: React.FC<AsyncSelectProps> = ({
  loadOptions,
  initialOptions = [],
  cacheOptions = true,
  loadingMessage = 'Loading...',
  errorMessage = 'Failed to load options',
  onFocus,
  ...selectProps
}) => {
  const [options, setOptions] = useState<SelectOption[]>(initialOptions);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const isLoadingRef = useRef(false);
  
  // Handle dropdown open/focus
  const handleFocus = useCallback(async () => {
    // Call parent onFocus if provided
    if (onFocus) {
      onFocus();
    }
    
    // Only load if not already loaded (or not caching)
    if (!hasLoaded || !cacheOptions) {
      // Prevent concurrent loading
      if (isLoadingRef.current) {
        return;
      }
      
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);
      
      try {
        const loadedOptions = await loadOptions();
        setOptions(loadedOptions);
        setHasLoaded(true);
      } catch (err) {
        console.error('Failed to load options:', err);
        setError(errorMessage);
        setOptions([]); // Clear options on error
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    }
  }, [loadOptions, hasLoaded, cacheOptions, onFocus, errorMessage]);
  
  // Reset loaded state when loadOptions changes
  React.useEffect(() => {
    setHasLoaded(false);
    if (!cacheOptions) {
      setOptions(initialOptions);
    }
  }, [loadOptions, cacheOptions, initialOptions]);
  
  // Prepare display options
  let displayOptions = options;
  if (loading) {
    displayOptions = [
      {
        value: '__loading__',
        label: loadingMessage,
        disabled: true
      }
    ];
  } else if (error) {
    displayOptions = [
      {
        value: '__error__',
        label: error,
        disabled: true
      }
    ];
  }
  
  return (
    <Select
      {...selectProps}
      options={displayOptions}
      onFocus={handleFocus}
      disabled={selectProps.disabled || loading}
    />
  );
};

export default AsyncSelect;